/**
 * D3 Planet Animation System - Main Export
 * 
 * Revolutionary sprite-based animation system for astrology chart planets
 * Leverages D3.js v7.8.5 with modern transitions and optimized performance
 */

// Import statements at the top
import { devLog } from '../../../../../utils/devLog';
import * as d3 from 'd3';
import { D3PlanetAnimator } from './D3PlanetAnimator';
import { D3PlanetSpriteManager } from './D3PlanetSpriteManager';
import { D3CornerHouseAnimator } from './D3CornerHouseAnimator';

// Core animation classes
export {
  D3PlanetAnimator,
  type PlanetSprite,
  type AnimationConfig
} from './D3PlanetAnimator';

export {
  D3PlanetSpriteManager,
  type PlanetSpriteConfig,
  type SpriteUpdateData
} from './D3PlanetSpriteManager';

export {
  D3CornerHouseAnimator,
  type CornerHouseConfig
} from './D3CornerHouseAnimator';


// Re-export optimized D3 utilities for convenience
export {
  useOptimizedZoom,
  batchD3Operations,
  useStableCallback
} from '../../../../../utils/optimization/optimizedD3';

/**
 * Convenience factory function to create a complete D3 animation system
 * 
 * @param svgContainer D3 selection of the SVG container
 * @param enableConsoleCommands Whether to enable browser console testing commands
 * @returns Object with all animation managers ready to use
 */
export function createD3AnimationSystem(
  svgContainer: d3.Selection<SVGSVGElement, unknown, any, any>,
  enableConsoleCommands: boolean = process.env.NODE_ENV === 'development'
) {
  // Create sprite manager
  const spriteManager = new D3PlanetSpriteManager(svgContainer);
  
  // Console commands removed (no test functions)

  return {
    spriteManager,
    animator: D3PlanetAnimator,
    cornerAnimator: D3CornerHouseAnimator,
    
    // Convenience methods
    createSprite: spriteManager.createPlanetSprite.bind(spriteManager),
    updateSprites: spriteManager.updateAllSprites.bind(spriteManager),
    animateSprite: D3PlanetAnimator.animatePlanetSprite,
    animateCornerMovement: D3CornerHouseAnimator.animateCornerHouseMovement,
    
    // Cleanup
    dispose: () => {
      spriteManager.dispose();
      if (process.env.NODE_ENV === 'development') {
        devLog('[D3AnimationSystem] Disposed');
      }
    }
  };
}

/**
 * Type definitions for the complete animation system
 */
export type D3AnimationSystem = ReturnType<typeof createD3AnimationSystem>;

/**
 * Export common animation presets for convenience
 */
export const ANIMATION_PRESETS = D3PlanetAnimator.ANIMATION_PRESETS;

/**
 * Export corner house utilities
 */
export const CORNER_HOUSE_UTILS = {
  isCornerHouse: D3CornerHouseAnimator.isCornerHouse,
  getCornerIndices: D3CornerHouseAnimator.getCornerHouseIndices,
  calculateRotation: D3CornerHouseAnimator.calculateCornerRotation,
  getCornerConfig: D3CornerHouseAnimator.getCornerConfig
};